import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { isFirstWilmaLoad, resetSurface, setWilmaTime } from "../../features/surface/surfaceSlice";
import { useGetLatestForecastQuery } from "../../services/riskSurface";
import { useSelectedRoute } from "../../features/riskViewer/riskViewerSlice";

/**
 * Custom hook to fetch the latest forecast data and manage its state.
 *
 * This hook retrieves the latest forecast time and handles setting the Wilma time
 * during the initial load. It also provides a function to refetch risk data by
 * resetting the surface with the latest forecast time.
 *
 * @returns {Object} - An object containing:
 *   - latestTime {Date|null}: The latest forecast time retrieved, or null if not available.
 *   - isLatestError {boolean}: Indicates if there was an error fetching the latest forecast.
 *   - latestSucceeded {boolean}: Indicates if the latest forecast fetch was successful.
 *   - refetchRiskData {Function}: Function to reset the surface with the latest time,
 *     skipping the update if latestTime is undefined.
 */
export const useLatestForecast = ( ) =>
{
    const dispatch = useDispatch();
    const routeId = useSelectedRoute();

    const {
        data: latestTime,
        isError: isLatestError,
        isSuccess: latestSucceeded,
    } = useGetLatestForecastQuery( undefined, {
        skip: routeId?.hexString !== undefined,
        pollingInterval: 1000 * 60 * 5, // Poll for updates every 5 minutes
    } );

    const isFirstLoad = isFirstWilmaLoad();

    useEffect( () =>
    {
        // If this is the first load and we have a valid latestTime, update the Wilma time
        if ( isFirstLoad && latestTime !== undefined )
        {
            dispatch( setWilmaTime( latestTime ) );
        }
    }, [ latestTime, isFirstLoad, dispatch ] );

    /**
     * Function to refetch risk data by resetting the surface with the latest time.
     * It will skip the update if latestTime is undefined.
     */
    const refetchRiskData = ( tauTimeout = false ) =>
    {
        if ( latestTime === undefined )
        {
            return; // Skipping update because latest time is undefined
        }
        dispatch( resetSurface( { forecastTime: latestTime, tauTimeout: tauTimeout } ) );
    };

    return { latestTime, isLatestError, latestSucceeded, refetchRiskData }; //TODO -- should isLatestError and isLatestSucceeded be separate?
};
